/*
  AIVIQ cart drawer
  - Opens on 'cart:open' (fired by the header's <toggle-cart>) and after 'ajaxProduct:added'
  - Every cart change goes through the Cart AJAX API with bundled section rendering, so the
    drawer and the theme's 'cart-ajax' section (cart bubble) update from one request
  - Only [data-acd-region] elements are swapped; discount input and terms stay intact
*/
(() => {
  if (customElements.get('aiviq-cart-drawer')) return

  // Event names from the theme's util.events.js
  const EV = {
    open: 'cart:open',
    close: 'cart:close',
    added: 'ajaxProduct:added',
    addError: 'ajaxProduct:error',
    updated: 'cart:updated',
    drawerClosed: 'headerDrawer:closed'
  }
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'

  class AiviqCartDrawer extends HTMLElement {
    connectedCallback() {
      // Retire the theme's old dropdown cart. Removing it runs its disconnectedCallback,
      // which drops its cart:open / ajaxProduct:added listeners, so only this drawer responds.
      document.querySelectorAll('header-drawer.site-header__cart').forEach((el) => el.remove())

      this.sectionId = this.dataset.sectionId
      this.panel = this.querySelector('.acd-panel')
      this.isOpen = false
      this.undo = null
      this.ac = new AbortController()
      const opts = { signal: this.ac.signal }

      document.addEventListener(EV.open, () => this.open(), opts)
      document.addEventListener(EV.close, () => this.close(), opts)
      document.addEventListener(EV.added, (e) => this.onProductAdded(e), opts)
      document.addEventListener(EV.addError, () => this.onAddError(), opts)
      document.addEventListener('keydown', (e) => this.onKeydown(e), opts)
      // Adding to the cart takes ~2 s on Shopify's side, so open straight away on "Tilføj til kurv"
      document.addEventListener('submit', (e) => this.onAddSubmit(e), { capture: true, signal: this.ac.signal })

      this.addEventListener('click', (e) => this.onClick(e), opts)
      this.querySelector('[data-acd-discount]')?.addEventListener('submit', (e) => this.onDiscount(e), opts)
      this.querySelector('[data-acd-terms]')?.addEventListener('change', () => this.toggleTermsError(false), opts)
      this.querySelector('[data-acd-checkout-form]')?.addEventListener('submit', (e) => this.onCheckout(e), opts)

      // Swipe right to close on touch screens (the upsell rail keeps its own sideways scroll)
      const passive = { passive: true, signal: this.ac.signal }
      this.panel.addEventListener('touchstart', (e) => {
        const t = e.touches[0]
        this.swipe = e.target.closest('.acd-rail, input') ? null : { x: t.clientX, y: t.clientY }
      }, passive)
      this.panel.addEventListener('touchend', (e) => {
        if (!this.swipe) return
        const t = e.changedTouches[0]
        const dx = t.clientX - this.swipe.x
        const dy = t.clientY - this.swipe.y
        this.swipe = null
        if (dx > 80 && Math.abs(dy) < 60) this.close()
      }, passive)
    }

    disconnectedCallback() {
      this.ac.abort()
    }

    /* ---------- open / close ---------- */

    open() {
      if (this.isOpen) return
      this.isOpen = true
      this.lastFocus = document.activeElement
      this.classList.add('is-open')
      document.documentElement.classList.add('acd-lock')
      if (window.lenis) window.lenis.stop()
      requestAnimationFrame(() => this.panel.focus({ preventScroll: true }))
    }

    close() {
      if (!this.isOpen) return
      this.isOpen = false
      this.classList.remove('is-open')
      document.documentElement.classList.remove('acd-lock')
      if (window.lenis) window.lenis.start()
      // Lets <toggle-cart> reset its own open state and aria-expanded. Dispatched from the
      // element (not document) because section.header.js reads evt.target.getAttribute('close').
      this.dispatchEvent(new CustomEvent(EV.drawerClosed, { bubbles: true }))
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true })
    }

    onKeydown(e) {
      if (!this.isOpen) return
      if (e.key === 'Escape') {
        this.close()
        return
      }
      if (e.key !== 'Tab') return
      const focusables = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])')].filter(
        (el) => el.offsetParent !== null
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    /* ---------- clicks ---------- */

    onClick(e) {
      const t = e.target
      if (t.closest('[data-acd-close]')) {
        e.preventDefault()
        this.close()
        return
      }

      const qtyBtn = t.closest('[data-acd-qty]')
      if (qtyBtn) {
        const item = qtyBtn.closest('.acd-item')
        const qty = Number(item.dataset.qty) + Number(qtyBtn.dataset.acdQty)
        if (qty >= 1) this.change(item, qty)
        return
      }

      const removeBtn = t.closest('[data-acd-remove]')
      if (removeBtn) {
        const item = removeBtn.closest('.acd-item')
        let properties = null
        try {
          properties = JSON.parse(item.dataset.properties || 'null')
        } catch (err) {}
        // Liquid renders "no properties" as [], which cart/add.js rejects; only keep a real object
        const line = { id: Number(item.dataset.variantId), quantity: Number(item.dataset.qty) }
        if (properties && !Array.isArray(properties) && Object.keys(properties).length) line.properties = properties
        this.undo = { line, title: item.dataset.title }
        this.change(item, 0)
        return
      }

      if (t.closest('[data-acd-undo]') && this.undo) {
        const { line } = this.undo
        this.undo = null
        this.querySelector('.acd-undo')?.remove()
        this.cartRequest('cart/add.js', { items: [line] }, line.id)
        return
      }

      const addBtn = t.closest('[data-acd-add]')
      if (addBtn) {
        const id = Number(addBtn.dataset.acdAdd)
        addBtn.disabled = true
        this.cartRequest('cart/add.js', { items: [{ id, quantity: 1 }] }, id)
        return
      }

      const railBtn = t.closest('[data-acd-rail]')
      if (railBtn) {
        this.querySelector('.acd-rail')?.scrollBy({ left: 164 * Number(railBtn.dataset.acdRail), behavior: 'smooth' })
        return
      }

      const codeBtn = t.closest('[data-acd-remove-code]')
      if (codeBtn) {
        const remaining = this.appliedCodes().filter((c) => c !== codeBtn.dataset.acdRemoveCode)
        this.cartRequest('cart/update.js', { discount: remaining.join(',') })
        return
      }

      const toggle = t.closest('[data-acd-toggle]')
      if (toggle) {
        const panel = this.querySelector('#' + toggle.getAttribute('aria-controls'))
        const expanded = toggle.getAttribute('aria-expanded') === 'true'
        toggle.setAttribute('aria-expanded', String(!expanded))
        panel.hidden = expanded
        if (!expanded) panel.querySelector('input')?.focus()
      }
    }

    change(item, quantity) {
      item.classList.add('is-loading')
      this.cartRequest('cart/change.js', { id: item.dataset.key, quantity })
    }

    /* ---------- discount, terms ---------- */

    appliedCodes() {
      const holder = this.querySelector('[data-codes]')
      return holder && holder.dataset.codes ? holder.dataset.codes.split(',').filter(Boolean) : []
    }

    async onDiscount(e) {
      e.preventDefault()
      const input = e.currentTarget.querySelector('input')
      const code = input.value.trim()
      const msg = this.querySelector('[data-acd-code-msg]')
      msg.hidden = false
      if (!code) {
        this.setMsg(msg, 'Skriv en rabatkode for at anvende den.', true)
        return
      }
      const codes = [...new Set([...this.appliedCodes(), code])]
      const cart = await this.cartRequest('cart/update.js', { discount: codes.join(',') })
      if (!cart) return
      const match = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase())
      if (match && match.applicable) {
        this.setMsg(msg, `${match.code} er anvendt.`, false)
        input.value = ''
      } else {
        // Drop a code that doesn't apply so it doesn't sit on the cart silently
        const keep = (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code)
        await this.cartRequest('cart/update.js', { discount: keep.join(',') })
        this.setMsg(msg, `Koden „${code}“ kan ikke bruges på denne kurv. Tjek stavningen og prøv igen.`, true)
      }
    }

    setMsg(el, text, isError) {
      el.textContent = text
      el.classList.toggle('is-error', isError)
    }

    toggleTermsError(show) {
      const err = this.querySelector('[data-acd-terms-error]')
      if (err) err.hidden = !show
      this.querySelector('.acd-terms')?.classList.toggle('is-error', show)
    }

    onCheckout(e) {
      const terms = this.querySelector('[data-acd-terms]')
      if (terms && !terms.checked) {
        e.preventDefault()
        this.toggleTermsError(true)
        terms.focus()
        return
      }
      e.currentTarget.querySelector('.acd-checkout')?.classList.add('is-loading')
    }

    /* ---------- cart requests + rendering ---------- */

    async cartRequest(path, body, highlightVariantId) {
      this.classList.add('is-busy')
      this.showError('')
      try {
        const res = await fetch(`${root}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: [this.sectionId, 'cart-ajax'], sections_url: window.location.pathname })
        })
        const data = await res.json()
        if (!res.ok) throw data
        if (data.sections) this.render(data.sections[this.sectionId], highlightVariantId)
        // add.js only returns the added lines; listeners (header bubble, product-card
        // quantity pickers) expect the whole cart, so fetch it before announcing the change
        let cart = data
        if (path === 'cart/add.js') {
          cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json())
          cart.sections = data.sections
        }
        document.dispatchEvent(new CustomEvent(EV.updated, { bubbles: true, detail: { cart } }))
        return cart
      } catch (err) {
        this.showError((err && (err.description || err.message)) || 'Noget gik galt. Prøv igen.')
        this.querySelectorAll('.is-loading').forEach((el) => el.classList.remove('is-loading'))
        this.querySelectorAll('[data-acd-add][disabled]').forEach((el) => (el.disabled = false))
        return null
      } finally {
        this.classList.remove('is-busy')
      }
    }

    onAddSubmit(e) {
      const form = e.target
      if (!(form instanceof HTMLFormElement) || this.contains(form)) return
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return
      // Only when the theme handles the add with Ajax; a plain form post leaves the page anyway
      setTimeout(() => {
        if (e.defaultPrevented) this.showPending()
      }, 0)
    }

    showPending() {
      this.pending = true
      this.classList.add('is-adding')
      const body = this.querySelector('[data-acd-region="body"]')
      if (body && !body.querySelector('.acd-pending')) {
        body.insertAdjacentHTML(
          'afterbegin',
          '<div class="acd-pending" role="status"><span class="acd-pending__tile"></span><span>Lægger i kurven …</span></div>'
        )
        body.scrollTop = 0
      }
      this.open()
      clearTimeout(this.pendingTimer)
      this.pendingTimer = setTimeout(() => this.clearPending(), 15000)
    }

    clearPending() {
      clearTimeout(this.pendingTimer)
      this.pending = false
      this.classList.remove('is-adding')
      this.querySelector('.acd-pending')?.remove()
    }

    onAddError() {
      const wasPending = this.pending
      this.clearPending()
      // The product page shows its own error; don't leave an empty drawer on top of it
      if (wasPending && Number(this.dataset.count) === 0) this.close()
    }

    async onProductAdded(e) {
      const detail = (e && e.detail) || {}
      const wasPending = this.pending
      const added = detail.product || {}
      // An add.js response carries the added line(s); a full cart (from update.js) has item_count
      const variantId =
        added.variant_id || (!('item_count' in added) && added.items && added.items[0] && added.items[0].variant_id)
      try {
        const res = await fetch(`${root}?sections=${encodeURIComponent(this.sectionId)}`)
        const json = await res.json()
        this.render(json[this.sectionId], variantId)
      } catch (err) {}
      this.clearPending()
      if (!detail.preventCartOpen || wasPending) this.open()
    }

    render(html, highlightVariantId) {
      if (!html) return
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('aiviq-cart-drawer')
      if (!fresh) return

      const body = this.querySelector('[data-acd-region="body"]')
      const scrollTop = body ? body.scrollTop : 0

      fresh.querySelectorAll('[data-acd-region]').forEach((region) => {
        const current = this.querySelector(`[data-acd-region="${region.dataset.acdRegion}"]`)
        if (current) current.innerHTML = region.innerHTML
      })

      const count = Number(fresh.dataset.count || 0)
      this.dataset.count = String(count)
      const foot = this.querySelector('[data-acd-foot]')
      if (foot) foot.hidden = count === 0 && !this.undo

      if (body) body.scrollTop = scrollTop
      if (this.undo) this.renderUndo()
      if (highlightVariantId) this.highlight(highlightVariantId)
    }

    renderUndo() {
      const body = this.querySelector('[data-acd-region="body"]')
      const bar = document.createElement('div')
      bar.className = 'acd-undo'
      bar.setAttribute('role', 'status')
      bar.innerHTML = `<span></span><button type="button" data-acd-undo>Fortryd</button>`
      bar.querySelector('span').textContent = `${this.undo.title} er fjernet.`
      const list = body.querySelector('.acd-items')
      list ? list.after(bar) : body.prepend(bar)
      clearTimeout(this.undoTimer)
      this.undoTimer = setTimeout(() => {
        this.undo = null
        bar.remove()
      }, 6000)
    }

    highlight(variantId) {
      const item = this.querySelector(`.acd-item[data-variant-id="${variantId}"]`)
      if (!item) return
      item.classList.add('is-new')
      const info = item.querySelector('.acd-item__info')
      if (info && !info.querySelector('.acd-tag--new')) {
        const tag = document.createElement('span')
        tag.className = 'acd-tag acd-tag--new'
        tag.innerHTML =
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="m5 12 5 5 9-10"/></svg>Tilføjet'
        info.append(tag)
      }
      setTimeout(() => {
        item.classList.remove('is-new')
        item.querySelector('.acd-tag--new')?.remove()
      }, 4000)
    }

    showError(text) {
      const el = this.querySelector('[data-acd-error]')
      if (!el) return
      el.textContent = text
      el.hidden = !text
    }
  }

  customElements.define('aiviq-cart-drawer', AiviqCartDrawer)
})()
